import { useMemo, useRef } from "react";

import { Box, Tabs } from "@mantine/core";
import { format } from "@react-input/mask";
import { useTranslation } from "react-i18next";

import { AppConstants } from "constant";
import { useFormAutosave } from "hooks";
import { BankCreditService } from "services";
import { displayClasses } from "styles";
import {
  BankCreditOptionsResponse,
  BankCreditRequestForm,
  BankCreditResponse,
  CreditResponse,
} from "types";
import { AppUtils } from "utils";

import { BankCreditRequestRequest } from "./bank-credit-request-request";
import { BankCreditRequestIncomes } from "./incomes/bank-credit-request-incomes";
import { BankCreditRequestOutcomes } from "./outcomes/bank-credit-request-outcomes";
import { BankCreditRequestPersonal } from "./personal/bank-credit-request-personal";

interface BankCreditRequestProps {
  credit: BankCreditResponse;
  options: BankCreditOptionsResponse;
  sourceCredit?: CreditResponse;
  readOnly?: boolean;
}

const TABS = {
  CREDIT: "credit",
  PERSONAL: "personal",
  INCOMES: "incomes",
  OUTCOMES: "outcomes",
};

export const BankCreditRequest = ({
  credit,
  options,
  sourceCredit,
  readOnly = false,
}: BankCreditRequestProps) => {
  const { t } = useTranslation();
  const sourceChildBirthYears = useMemo(
    () => sourceCredit?.children?.map((child) => child.birthYear),
    [sourceCredit],
  );

  // Ref to trigger employment date validation from personal info tab
  const revalidateEmploymentDatesRef = useRef<
    ((newBirthDate?: Date | undefined | null, newVisaFrom?: Date | undefined | null) => void) | null
  >(null);

  const { form, visibleFields, updateCredit } = useFormAutosave<BankCreditRequestForm>({
    credit,
    updateService: BankCreditService.bankCreditUpdateMultipleProperties,
    getVisibleFieldsFn: AppUtils.getVisibleBankRequestFields,
    readOnly,
    initialValuesTransform: (credit: BankCreditResponse) => ({
      ...credit,
      partnerPhoneNumber: credit.partnerPhoneNumber ? format(credit.partnerPhoneNumber, { mask: AppConstants.MASK_SWISS_PHONE_NUMBER, replacement: { _: /\d/ } }) : credit.partnerPhoneNumber,
      user: {
        ...credit.user,
        firstName: credit.user.fullName.split(" ")[0],
        lastName: credit.user.fullName.split(" ")[1],
        phone: credit.user.countryCode + " " + credit.user.phoneNumber,
      },
      birthDate: credit.birthDate ? new Date(credit.birthDate) : undefined,
      visaFrom: credit.visaFrom ? new Date(credit.visaFrom) : undefined,
      visaTo: credit.visaTo ? new Date(credit.visaTo) : undefined,
      livingFrom: credit.livingFrom ? new Date(credit.livingFrom) : undefined,
      partnerBirthDate: credit.partnerBirthDate ? new Date(credit.partnerBirthDate) : undefined,
      partnerVisaFrom: credit.partnerVisaFrom ? new Date(credit.partnerVisaFrom) : undefined,
      partnerVisaTo: credit.partnerVisaTo ? new Date(credit.partnerVisaTo) : undefined,
      saturdayAvailability: Boolean(
        credit.saturdayAvailabilityFrom || credit.saturdayAvailabilityTo,
      ),
      partnerSaturdayAvailability: Boolean(
        credit.partnerSaturdayAvailabilityFrom || credit.partnerSaturdayAvailabilityTo,
      ),
      additionalExpenses: Boolean(
        credit.expenses?.[0]?.expenseType || credit.expenses?.[0]?.amount,
      ),
      creditLanguage:
        credit.creditLanguage || AppUtils.getCreditLanguage(credit.nationality, credit.canton),
      mortgageOwnedSince: credit.mortgageOwnedSince
        ? new Date(credit.mortgageOwnedSince)
        : undefined,
      children: credit.children?.map((child) => ({
        ...child,
        birthdate: child.birthdate ? new Date(child.birthdate) : undefined,
      })),
    }),
  });

  const tabs = useMemo(
    () => [
      {
        value: TABS.CREDIT,
        component: (
          <BankCreditRequestRequest
            credit={credit}
            form={form}
            options={options}
            visibleFields={visibleFields}
            readOnly={readOnly}
          />
        ),
      },
      {
        value: TABS.PERSONAL,
        component: (
          <BankCreditRequestPersonal
            credit={credit}
            form={form}
            options={options}
            visibleFields={visibleFields}
            sourceChildBirthYears={sourceChildBirthYears}
            readOnly={readOnly}
            updateCredit={updateCredit}
            revalidateEmploymentDatesRef={revalidateEmploymentDatesRef}
          />
        ),
      },
      {
        value: TABS.INCOMES,
        component: (
          <BankCreditRequestIncomes
            credit={credit}
            form={form}
            options={options}
            readOnly={readOnly}
            revalidateEmploymentDatesRef={revalidateEmploymentDatesRef}
          />
        ),
      },
      {
        value: TABS.OUTCOMES,
        component: (
          <BankCreditRequestOutcomes
            credit={credit}
            form={form}
            visibleFields={visibleFields}
            options={options}
            readOnly={readOnly}
          />
        ),
      },
    ],
    [form, options, visibleFields, readOnly, credit, sourceChildBirthYears, updateCredit],
  );

  return (
    <Box style={{ border: "0.5px solid #B046AC", borderRadius: 16 }}>
      <Tabs defaultValue={tabs[0].value} variant={"pills"} mt={"lg"}>
        <Tabs.List grow={true} px="md">
          {tabs.map((tab) => (
            <Tabs.Tab key={tab.value} value={tab.value} className={displayClasses.tabView}>
              {t("credit.view.tab." + tab.value)}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {tabs.map((tab) => (
          <Tabs.Panel key={tab.value} value={tab.value} className={displayClasses.tabContent}>
            {tab.component}
          </Tabs.Panel>
        ))}
      </Tabs>
    </Box>
  );
};
